import React, { ChangeEvent, useEffect, useState } from 'react'
import { Button, FormField, ImageBuffer, Loader, SubCover, SubmitModal } from '../..'
import { PhotoRounded } from '@mui/icons-material'
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../reducers/store';
import { adminValidateSelectedFile } from '../../validations/admins/register';
import { actionUpdateAdmin } from '../../../actions/admins';
import toast from 'react-hot-toast';
import { create } from '../../../assets';

import './editAdminImage.css'

interface PostDataParams {
  selectedFile?: File | null;
}

const EditAdminImage = ({id, details}: any) => {
  const pageName                                    = "Edit Admin Image"
  const dispatch                                    = useDispatch<AppDispatch>();
  const [hideShow, setHideShow]                     = useState<boolean>(false);
  const [savingInfo, setSavingInfo]                 = useState<boolean>(false);
  const [fileNames, setFileNames]                   = useState<{ [key: string]: string }>({});
  const [onOpen, setOnOpen]                         = useState<boolean>(false);
  const [userImg, setUserImg]                       = useState<string>(`${process.env.REACT_APP_BACKEND_ADMIN_IMAGE_URL}${details?.selectedFile}`);
  const [selectedFileErrors, setSelectedFileErrors] = useState<string[]>([]);
  const [isButtonDisabled, setIsButtonDisabled]     = useState<boolean>(true);
  const [errorMessage, setErrorMessage]             = useState(null);
  const [openSubMegaMenu, setOpenSubMegaMenu]       = useState<boolean>(false);
  const [postData, setPostData]                   = useState<PostDataParams>({ selectedFile: null})
  const [isLoadingBtn, setIsLoadingBtn]           = useState({ selectedFile: false});
  const [isValid, setIsValid]                     = useState({ selectedFile: false});

  const useDebounce = <T,>(value: T, delay: number): T => {
    const [debounced, setDebounced] = useState(value);
  
    useEffect(() => {
      const handler = setTimeout(() => {
        setDebounced(value);
      }, delay);
      return () => clearTimeout(handler);
    }, [value, delay]);
  
    return debounced;
  };

  const debouncedPostData = useDebounce<PostDataParams>(postData, 500);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
  
      setFileNames(prev => ({
        ...prev,
        [e.target.name]: file.name,
      }));
  
      // Store RAW file
      setPostData(prev => ({
        ...prev,
        selectedFile: file,
      }));
  
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setUserImg(previewUrl);
  };
  
  const handleUserImageUpload = (e: any) => {
    const file = e.target.files[0]

    transformFile(file)
  }

  const transformFile = (file: File | null) => {
    const reader = new FileReader();
  
    if (file) {
      reader.readAsDataURL(file);
  
      reader.onloadend = () => {
        const result = reader.result;
  
        // Ensure the result is a string before updating state
        if (typeof result === 'string') {
          setUserImg(result);
        }
      };
    } else {
      setUserImg(''); // Reset the array if no file is passed
    }
  };

  useEffect(() => {
    const validateSelectedFile = () => {
      const { isValid, errors } = adminValidateSelectedFile({ postData: debouncedPostData });
      setIsValid((prevState) => ({
          ...prevState,
          selectedFile: isValid,
      }));
      setSelectedFileErrors(errors);
      setIsLoadingBtn((prevState) => ({
          ...prevState,
          selectedFile: false,
      }));
      return errors;
    };

    const selectedFileErrors     = validateSelectedFile();
    
    setSelectedFileErrors(selectedFileErrors);

    const hasFormErrors = selectedFileErrors.length > 0;
    setIsButtonDisabled(hasFormErrors);
  }, [debouncedPostData]);

  const handleModalSubmit = () => {
    setOnOpen(true)
    setOpenSubMegaMenu(true)
  }

  const handleCloseCover = () => {
    setOnOpen(false) // modal
    setOpenSubMegaMenu(false)
  }

  const handleSubmit = async(e: ChangeEvent<any>) => {
    e.preventDefault();
    handleModalSubmit()
  }

  const cancelPostCreation = () => {
    setOnOpen(false); // Close the modal
    setSavingInfo(false);
    setOpenSubMegaMenu(false)
  };

  const confirmPostCreation = async() => {
    setSavingInfo(true);
    setIsButtonDisabled(true);
    const formData = new FormData();

    Object.entries(postData).forEach(([key, value]) => {
      if (value !== null) {
          formData.append(key, value as any);
      }
    });
    const res = await dispatch(actionUpdateAdmin({id, postData: formData}))
    try{
        if (res.success === true) {
        // Password reset successful, show success message from response
        toast.success("post updated successfully");
        setOpenSubMegaMenu(false)
        setErrorMessage(null); // Clear any previous error message
        setPostData({selectedFile: res?.data?.selectedFile || null})
        setSavingInfo(false)
        setFileNames({});
        } else if(res.status === 400) {
          setErrorMessage(res?.data.message);
          setSavingInfo(false)
          setOpenSubMegaMenu(false)
        }
    }catch(error){
        setErrorMessage(res.status)
        setSavingInfo(false)
        setOpenSubMegaMenu(false)
    }
    setOnOpen(false); // Close the modal after confirmation
  };
    
  return (
    <>
      <title>{pageName}</title>
      <div className='edit-detail-admin-image-container'>
        <form className='' onSubmit={handleSubmit} autoComplete="off" >
          <div className='edit-detail-admin-image-title'>
            <h3>update Admin Image</h3>
          </div>

          <div className=''>
            <div className='reg-image-group'>
              <div onChange={handleUserImageUpload}>
                <FormField fileInputType name="selectedFile" selectedLabelName="Upload your Photo" accept="image/*" handleChange={handleImageUpload} selectedFileName={fileNames.selectedFile} isLoadingBtn={isLoadingBtn.selectedFile} isValid={isValid.selectedFile} errors={selectedFileErrors || []} />
              </div>
              <div className='reg-image-preview-wrapper'>
                {userImg ? <ImageBuffer src={userImg} alt='userImg' />  : <span><PhotoRounded /></span>}
              </div>
            </div>

            <SubmitModal onOpen={onOpen} onClose={cancelPostCreation} onConfirm={confirmPostCreation} modalImage={create} prompt="Are you sure you want to create post" />
            <Button onClickButton buttonClickWrap={savingInfo ? `button-login-submitted` : `button-login-submit`} onClickName={savingInfo ? <>{<Loader />} updating data...</> : "Update Admin"} isButtonDisabled={isButtonDisabled} buttonClasses={savingInfo ? ['button-disabled'] : (isButtonDisabled ? ['buttonDisabledClass'] : ['buttonEnabledClass'])} disabled={savingInfo} />
            
            {errorMessage && (
              <div className="form-response-message">
              <p className="error-msg">{errorMessage}</p>
              </div>
            )}
          </div>
        </form>
      </div>

      {openSubMegaMenu && (
        //z-index: 3, others should be higher
        <SubCover handleCloseCover={handleCloseCover} />
      )}
    </>
  )
}

export default EditAdminImage